import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SelectorImagenesGaleria from './SelectorImagenesGaleria';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import '../../styles/admin/Forms.css'; // Usaremos un CSS común para formularios

// Componente para un elemento arrastrable (imagen)
const DraggableImageItem = ({ index, url, moveImage, isDelete, onDelete, badge, info, disabled, title }) => {
  const [{ isDragging }, dragRef] = useDrag({
    type: 'IMAGE_ITEM',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disabled,
  });

  const [, dropRef] = useDrop({
    accept: 'IMAGE_ITEM',
    hover: (item, monitor) => {
      if (item.index === index) {
        return;
      }
      moveImage(item.index, index);
      item.index = index;
    },
  });

  return (
    <div 
      ref={(node) => dragRef(dropRef(node))} 
      className={`image-preview-item ${isDelete ? 'marked-for-deletion' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <img src={url} alt={`Imagen ${index + 1}`} />
      <button 
        type="button" 
        onClick={onDelete} 
        className="btn-remove-single-image"
        disabled={disabled}
        title={title}
      >
        &times;
      </button>
      {badge && <span className={badge.className}>{badge.text}</span>}
      {info && (
        <div className="image-tag">
          {info}
        </div>
      )}
    </div>
  );
};

function CategoriaForm({ onSubmit, initialData = null, onCancel }) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [orden, setOrden] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // --- Estados para Imágenes Múltiples --- 
  const [existingImageUrls, setExistingImageUrls] = useState([]); // URLs de imágenes ya guardadas (editando)
  const [newImageFiles, setNewImageFiles] = useState([]); // Nuevos archivos seleccionados (File objects)
  const [newImagePreviews, setNewImagePreviews] = useState([]); // Previews de nuevos archivos (Data URLs)
  const [imagesToDelete, setImagesToDelete] = useState([]); // URLs de imágenes existentes marcadas para borrar
  
  // --- Estados para Imágenes de Galería ---
  const [galeriaSelectedImages, setGaleriaSelectedImages] = useState([]); // Imágenes seleccionadas de la galería

  // Si hay datos iniciales (modo edición), llenar el formulario
  useEffect(() => {
    if (initialData) {
      setNombre(initialData.nombre || '');
      setDescripcion(initialData.descripcion || '');
      setOrden(initialData.orden !== undefined && initialData.orden !== null ? String(initialData.orden) : '');
      setExistingImageUrls(Array.isArray(initialData.imagenes) ? initialData.imagenes : []);
      // Resetear campos de nuevas imágenes y borrado
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setImagesToDelete([]);
      setGaleriaSelectedImages([]);
    } else {
      // Resetear si no hay datos iniciales (modo añadir)
      setNombre('');
      setDescripcion('');
      setOrden('');
      setExistingImageUrls([]);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setImagesToDelete([]);
      setGaleriaSelectedImages([]);
    }
  }, [initialData]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Añadir nuevos archivos al estado
      setNewImageFiles(prevFiles => [...prevFiles, ...files]);
      
      // Generar previews para los nuevos archivos
      const filePromises = files.map(file => {
          return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve({ file, preview: reader.result });
              reader.onerror = reject;
              reader.readAsDataURL(file);
          });
      });

      Promise.all(filePromises).then(newPreviewsData => {
          setNewImagePreviews(prevPreviews => [
              ...prevPreviews,
              ...newPreviewsData.map(data => data.preview) // Guardar solo las Data URLs
          ]);
      }).catch(err => {
          console.error("Error generating image previews:", err);
          setError('Error al previsualizar una o más imágenes.');
      });
    }
    // Limpiar el valor del input para permitir seleccionar los mismos archivos si se deseleccionan
    e.target.value = null;
  };

  const handleRemoveNewImage = (index) => {
      // Crear una copia del array de archivos y eliminar el elemento en el índice
      const newFiles = [...newImageFiles];
      newFiles.splice(index, 1);
      setNewImageFiles(newFiles);
      
      // Hacer lo mismo con los previews
      const newPreviews = [...newImagePreviews];
      newPreviews.splice(index, 1);
      setNewImagePreviews(newPreviews);
  };

  const handleMarkForDeletion = (imageUrl) => {
      // Añadir a la lista de URLs a borrar
          setImagesToDelete(prev => [...prev, imageUrl]);
      // Eliminar de las imágenes existentes
      setExistingImageUrls(prev => prev.filter(url => url !== imageUrl));
  };
  
  // Handler para imágenes seleccionadas de la galería
  const handleGaleriaImagesSelected = (selectedImages) => {
    setGaleriaSelectedImages(selectedImages);
  };

  // Funciones para reordenar imágenes con drag & drop
  const moveExistingImage = (fromIndex, toIndex) => {
    const updatedUrls = [...existingImageUrls];
    const [movedItem] = updatedUrls.splice(fromIndex, 1);
    updatedUrls.splice(toIndex, 0, movedItem);
    setExistingImageUrls(updatedUrls);
  };

  const moveNewImage = (fromIndex, toIndex) => {
    // Mover archivo
    const updatedFiles = [...newImageFiles];
    const [movedFile] = updatedFiles.splice(fromIndex, 1);
    updatedFiles.splice(toIndex, 0, movedFile);
    setNewImageFiles(updatedFiles);
    
    // Mover preview
    const updatedPreviews = [...newImagePreviews];
    const [movedPreview] = updatedPreviews.splice(fromIndex, 1);
    updatedPreviews.splice(toIndex, 0, movedPreview);
    setNewImagePreviews(updatedPreviews);
  };

  const moveGalleryImage = (fromIndex, toIndex) => {
    const updatedImages = [...galeriaSelectedImages];
    const [movedImage] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedImage);
    setGaleriaSelectedImages(updatedImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre) {
      setError('El nombre de la categoría es obligatorio.');
      return;
    }
    setIsSubmitting(true);
    setError('');

    const categoriaData = {
      nombre,
      descripcion,
      orden: orden === '' ? null : (isNaN(parseInt(orden)) ? null : parseInt(orden)),
      // El array 'imagenes' se calculará y guardará en la función onSubmit del padre
    };

    try {
      // Extraer las URLs de las imágenes de la galería
      const galeriasUrls = galeriaSelectedImages.map(img => img.url);
      
      // Ya no usamos imagesToDelete aquí, lo pasamos directamente al handler padre
      // También pasamos las URLs existentes EN EL ORDEN ACTUAL (después del drag & drop)
      
      if (initialData) {
        // En modo edición
        await onSubmit(
          categoriaData, 
          newImageFiles,          // Nuevos archivos (array ordenado)
          imagesToDelete,         // URLs a borrar
          galeriasUrls,           // URLs de galería (array ordenado)
          existingImageUrls       // Las URLs existentes en su orden actual
        );
      } else {
        // En modo añadir
        await onSubmit(
          categoriaData, 
          newImageFiles,          // Nuevos archivos (array ordenado)
          [],                     // No hay imágenes para borrar en modo añadir
          galeriasUrls,           // URLs de galería (array ordenado)
          []                      // No hay imágenes existentes en modo añadir
        );
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(err.message || 'Ocurrió un error al guardar la categoría.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="admin-form">
      <h3>{initialData ? 'Editar Categoría' : 'Añadir Nueva Categoría'}</h3>
      {error && <p className="error-message">{error}</p>}
      
      <div className="form-group">
        <label htmlFor="cat-nombre">Nombre *</label>
        <input
          id="cat-nombre"
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="cat-descripcion">Descripción</label>
        <textarea
          id="cat-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="cat-orden">Orden (número)</label>
        <input
          id="cat-orden"
          type="number"
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          placeholder="Ej: 1 (menor número aparece primero)"
          disabled={isSubmitting}
        />
      </div>

      <div className="form-group">
        <label>Imágenes</label>
        
        {/* Sección para subir nuevas imágenes */}
        <div className="upload-methods">
          <div className="upload-method">
            <h4>Subir nuevas imágenes</h4>
            <input
              id="cat-imagenes"
              type="file"
              accept="image/png, image/jpeg, image/webp"
              multiple
              onChange={handleImageChange}
              disabled={isSubmitting}
            />
          </div>
          
          {/* Selector de imágenes de la galería */}
          <div className="upload-method">
            <h4>Seleccionar de la galería</h4>
            <SelectorImagenesGaleria 
              onImagesSelected={handleGaleriaImagesSelected}
              initialSelectedImages={[]}
              multiple={true}
            />
          </div>
        </div>

        <DndProvider backend={HTML5Backend}>
          {/* Previsualización de imágenes existentes (en modo edición) */}
          {initialData && existingImageUrls.length > 0 && (
            <div className="image-previews-container">
              <h4>Imágenes actuales: <small>(Arrastra para reordenar)</small></h4>
              <div className="image-previews-grid">
                {existingImageUrls.map((url, index) => (
                  <DraggableImageItem
                    key={index}
                    index={index}
                    url={url}
                    moveImage={moveExistingImage}
                    onDelete={() => handleMarkForDeletion(url)}
                    info={`#${index + 1}`}
                    disabled={isSubmitting}
                    title="Marcar para borrar"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Previsualización de nuevas imágenes para subir */}
          {newImagePreviews.length > 0 && (
            <div className="image-previews-container">
              <h4>Nuevas imágenes a subir: <small>(Arrastra para reordenar)</small></h4>
              <div className="image-previews-grid">
                {newImagePreviews.map((preview, index) => (
                  <DraggableImageItem
                    key={`new-${index}`}
                    index={index}
                    url={preview}
                    moveImage={moveNewImage}
                    onDelete={() => handleRemoveNewImage(index)}
                    badge={{ className: "new-image-badge", text: "Nueva" }}
                    disabled={isSubmitting}
                    title="Quitar imagen"
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Mostrar imágenes seleccionadas de la galería */}
          {galeriaSelectedImages.length > 0 && (
            <div className="image-previews-container">
              <h4>Imágenes seleccionadas de la galería: <small>(Arrastra para reordenar)</small></h4>
              <div className="gallery-images-grid">
                {galeriaSelectedImages.map((image, index) => (
                  <DraggableImageItem
                    key={`gallery-${index}`}
                    index={index}
                    url={image.url}
                    moveImage={moveGalleryImage}
                    onDelete={() => setGaleriaSelectedImages(prev => prev.filter((_, i) => i !== index))}
                    badge={{ className: "gallery-image-badge", text: "Galería" }}
                    info={image.nombre}
                    disabled={isSubmitting}
                    title="Quitar de selección"
                  />
                ))}
              </div>
            </div>
          )}
        </DndProvider>
      </div>

      <div className="form-actions">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : (initialData ? 'Actualizar Categoría' : 'Añadir Categoría')}
        </button>
        {onCancel && (
           <button type="button" onClick={onCancel} disabled={isSubmitting} className="cancel-button">
             Cancelar
           </button>
        )}
      </div>
    </form>
  );
}

CategoriaForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object, // Ahora debe incluir { ..., imagenes: [...] } 
  onCancel: PropTypes.func, 
};

export default CategoriaForm; 